import {
  approveRxChange,
  denyRxChange,
  rxChangeAction as rxChangeActionEndpoint,
} from '@lotushealth/lotushealth_js_sdk/endpoints';
import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import type {
  ApproveRxChangeRequestT,
  DenyRxChangeRequestT,
  IdentifierResponseT,
  ResponseT,
  RxChangeAction200,
  RxChangeActionBody,
  RxChangeApproveWithChangesActionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

/**
 * Typed transport for the three RxChange action endpoints. ``approve`` and
 * ``deny`` hit the dedicated DoseSpot-shaped endpoints; ``approveWithChanges``
 * hits the discriminated copilot ``rxChangeAction`` endpoint, where the
 * ``action`` field is forced to ``"approve_with_changes"`` regardless of
 * caller input. Mutation hooks should consume this module rather than wiring
 * axios directly.
 */
export const rxChangeAction = {
  async approve(
    rxChangeId: number,
    data: ApproveRxChangeRequestT,
    signal?: AbortSignal
  ): Promise<IdentifierResponseT> {
    const config = await createLotusRequestConfig({ signal });
    const response = await approveRxChange(rxChangeId, data, config);
    return lotusHealthZod.approveRxChangeResponse.parse(response.data) as IdentifierResponseT;
  },

  async deny(
    rxChangeId: number,
    data: DenyRxChangeRequestT,
    signal?: AbortSignal
  ): Promise<ResponseT> {
    const config = await createLotusRequestConfig({ signal });
    const response = await denyRxChange(rxChangeId, data, config);
    return lotusHealthZod.denyRxChangeResponse.parse(response.data) as ResponseT;
  },

  async approveWithChanges(
    rxChangeId: number,
    data: RxChangeApproveWithChangesActionT,
    signal?: AbortSignal
  ): Promise<RxChangeAction200> {
    const body: RxChangeActionBody = { ...data, action: 'approve_with_changes' };
    const config = await createLotusRequestConfig({ signal });
    const response = await rxChangeActionEndpoint(rxChangeId, body, config);
    return lotusHealthZod.rxChangeActionResponse.parse(response.data) as RxChangeAction200;
  },
} as const;
